/**
 * FilterPanel.tsx — Ex-US Professionals V3
 * Filter bar: Universe, Degree, Specialty with Apply / Clear buttons.
 */
import React from 'react';
import { UseFormRegister } from 'react-hook-form';
import styles from '../ExUsProfessionalsV3.module.css';
import type { SearchFormValues } from './SearchPanel';
import degreesJson from '../../../lookup/degrees.json';
import specialtiesJson from '../../../lookup/specialties.json';

interface Props {
    register:     UseFormRegister<SearchFormValues>;
    onApply:      () => void;
    onClearFilter: () => void;
    isLoading:    boolean;
}

const degreeOptions = Object.entries(degreesJson as Record<string, string>).map(
    ([value, label]) => ({ value, label })
).sort((a, b) => a.value.localeCompare(b.value));

const specialtyOptions = Object.entries(specialtiesJson as Record<string, string>).map(
    ([value, label]) => ({ value, label })
).sort((a, b) => a.value.localeCompare(b.value));

const universeOptions = [
    { value: '',      label: '— All —' },
    { value: 'MyTer', label: 'My Territory' },
    { value: 'SPEND', label: 'Spend' },
    { value: 'CRM',   label: 'CRM' },
];

export default function FilterPanel({ register, onApply, onClearFilter, isLoading }: Props) {
    return (
        <div className={styles.filterPanel}>
            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Universe</label>
                <select
                    className={styles.fieldSelect}
                    {...register('universe')}
                    data-testid="field-universe"
                >
                    {universeOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Degree</label>
                <select
                    className={styles.fieldSelect}
                    {...register('degree')}
                    data-testid="field-degree"
                >
                    <option value="">— All —</option>
                    {degreeOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.value}</option>
                    ))}
                </select>
            </div>

            <div className={styles.searchField}>
                <label className={styles.fieldLabel}>Specialty</label>
                <select
                    className={styles.fieldSelect}
                    {...register('specialty')}
                    data-testid="field-specialty"
                    style={{ minWidth: 160 }}
                >
                    <option value="">— All —</option>
                    {specialtyOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.value}</option>
                    ))}
                </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginLeft: 8 }}>
                <button
                    type="button"
                    onClick={onApply}
                    disabled={isLoading}
                    data-testid="btn-applyFilter"
                    style={{
                        background: '#fff',
                        color: '#3d3e8e',
                        border: '1px solid #fff',
                        borderRadius: 3,
                        padding: '6px 16px',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                    }}
                >
                    APPLY FILTER
                </button>
                <button
                    type="button"
                    onClick={onClearFilter}
                    disabled={isLoading}
                    data-testid="btn-clearFilter"
                    style={{
                        background: 'transparent',
                        color: '#fff',
                        border: '1px solid #fff',
                        borderRadius: 3,
                        padding: '6px 14px',
                        fontSize: 13,
                        cursor: 'pointer',
                    }}
                >
                    CLEAR FILTER
                </button>
            </div>
        </div>
    );
}
